import { LEAD_CHANNEL_LABELS } from './leadsConfig';
import {
  LEAD_PROFILE_CHANNEL_OPTIONS,
  LEAD_PROFILE_EMPLOYMENT_OPTIONS,
  LEAD_PROFILE_HOUSING_OPTIONS,
  LEAD_PROFILE_TIME_OPTIONS,
} from './leadProfile';
import { LEAD_PF_ZONE_MAX, pfZoneToken } from '../services/leadsService';

/** “Ordenar por” (server-side, `getleads?sort=`). */
export const LEAD_SORT_LABELS = {
  last_touch_desc: 'Último contacto',
  updated_desc: 'Actividad reciente',
  created_desc: 'Alta más nueva',
  created_asc: 'Alta más antigua',
  last_inquiry_desc: 'Última consulta',
  name_asc: 'Nombre (A–Z)',
};
/** Default del panel: última actividad de cliente o corredor (temperatura V3). */
export const LEAD_DEFAULT_SORT = 'last_touch_desc';

/** Canal en el filtro: los del lead + “Sin canal” (`none`). */
export const LEAD_CHANNEL_FILTER_OPTIONS = [
  { value: 'share_form', label: 'Link compartido' },
  ...['whatsapp', 'phone', 'in_person', 'social', 'portal'].map((value) => ({ value, label: LEAD_CHANNEL_LABELS[value] })),
  { value: 'other', label: 'Otro canal' },
  { value: 'none', label: 'Sin canal' },
];

/**
 * Tipo de interés (histórico: salió de Filtros avanzados; el servicio
 * `getLeads` sigue aceptando `interestKinds` si hace falta).
 */
export const LEAD_INTEREST_FILTER_OPTIONS = [
  { value: 'unit', label: 'Unidad', kinds: ['user_lot'] },
  { value: 'lot', label: 'Lote', kinds: ['cadastral_lot', 'cad_parcel'] },
  { value: 'list', label: 'Lista', kinds: ['list', 'showroom'] },
];

/**
 * Atajos de “Última actividad” (`updated_*`, día AR). Los de rango terminan
 * hoy; `stale30` = sin actividad hace +30 días (solo `updated_to`).
 * Un atajo activo deja Desde/Hasta vacíos: el rango se calcula al consultar.
 */
export const LEAD_DATE_PRESETS = [
  { value: 'today', label: 'Hoy', summary: 'hoy' },
  { value: '7d', label: 'Últimos 7 días', summary: 'últimos 7 días' },
  { value: '30d', label: 'Últimos 30 días', summary: 'últimos 30 días' },
  { value: 'month', label: 'Este mes', summary: 'este mes' },
  { value: 'stale30', label: 'Sin actividad hace +30 días', summary: 'Sin actividad +30 días', standalone: true },
];

const AR_TZ = 'America/Argentina/Buenos_Aires';

/** Hoy en AR como YYYY-MM-DD (el día que usa la API). */
export function getLeadTodayAR(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: AR_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

const shiftDay = (iso, days) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
};

/** Rango {from,to} de un atajo; `null` si no hay atajo (rango a mano). */
export function getLeadDatePresetRange(preset, today = getLeadTodayAR()) {
  switch (preset) {
    case 'today': return { from: today, to: today };
    case '7d': return { from: shiftDay(today, -6), to: today };
    case '30d': return { from: shiftDay(today, -29), to: today };
    case 'month': return { from: `${today.slice(0, 8)}01`, to: today };
    case 'stale30': return { from: '', to: shiftDay(today, -30) };
    default: return null;
  }
}

/**
 * Ajusta Desde/Hasta para que nunca queden inválidos: sin fechas futuras,
 * Desde ≤ Hasta. `changed` es el campo que tocó el usuario (gana él).
 */
export function clampLeadDateRange({ from = '', to = '' }, changed, today = getLeadTodayAR()) {
  let f = validDate(from);
  let t = validDate(to);
  if (f > today) f = today;
  if (t > today) t = today;
  if (f && t && f > t) {
    if (changed === 'to') f = t; else t = f;
  }
  return { from: f, to: t };
}

// ── Datos del cliente (`pf_*`) ────────────────────────────────────────────

/**
 * Mostrar la sección «Datos del cliente» en Filtros. La API vieja **ignora**
 * los `pf_*` (listado sin filtrar, sin error): si el Front sale antes que la
 * API con los filtros, poner en `false` hasta el deploy de la API.
 */
export const SHOW_LEAD_PROFILE_FILTERS = true;

/**
 * Edad: bandas de **selección única** (más «Cualquiera»). La API acepta un solo
 * rango (`pf_age_min` / `pf_age_max`); una banda = un rango exacto, sin
 * uniones que elijan de más.
 */
export const LEAD_PROFILE_AGE_BANDS = [
  { value: 'lt30', label: 'Hasta 29', min: null, max: 29 },
  { value: '30_44', label: '30–44', min: 30, max: 44 },
  { value: '45_59', label: '45–59', min: 45, max: 59 },
  { value: 'gte60', label: '60+', min: 60, max: null },
];

const NONE_OPTION = { value: 'none', label: 'Sin dato' };
/** Grupos de chips multi-selección (etiquetas de `leadProfile.js` + «Sin dato» = `none`). */
export const LEAD_PROFILE_FILTER_GROUPS = [
  { key: 'employment', label: 'Situación laboral', summary: 'Laboral', options: [...LEAD_PROFILE_EMPLOYMENT_OPTIONS, NONE_OPTION] },
  { key: 'contactTimes', label: 'Horario de contacto', summary: 'Horario', options: [...LEAD_PROFILE_TIME_OPTIONS, NONE_OPTION] },
  { key: 'contactChannels', label: 'Prefiere que lo contacten por', summary: 'Contacto', options: [...LEAD_PROFILE_CHANNEL_OPTIONS, NONE_OPTION] },
  {
    key: 'housing',
    label: 'Vivienda',
    summary: 'Vivienda',
    options: [
      ...LEAD_PROFILE_HOUSING_OPTIONS.map((o) => ({ ...o, label: o.value === 'first_home' ? 'Primera vivienda' : 'Propietario/a' })),
      NONE_OPTION,
    ],
  },
];

export const EMPTY_LEAD_PROFILE_FILTERS = Object.freeze({
  age: '',
  employment: [],
  contactTimes: [],
  contactChannels: [],
  housing: [],
  /** Items de `/searchplacenames` + `{ kind: 'other' }` / `{ kind: 'none' }`. */
  zones: [],
});

export const EMPTY_LEAD_ADVANCED_FILTERS = Object.freeze({
  datePreset: '',
  from: '',
  to: '',
  channels: [],
  profile: EMPTY_LEAD_PROFILE_FILTERS,
});

/** Param `pf_*` de un 400 → clave del estado de filtros de perfil (o `''` = todos). */
export const LEAD_PROFILE_FILTER_FIELD_KEYS = {
  pf_employment_status: 'employment',
  pf_contact_time: 'contactTimes',
  pf_contact_channel: 'contactChannels',
  pf_housing_status: 'housing',
  pf_zone: 'zones',
  pf_age_min: 'age',
  pf_age_max: 'age',
};
const PROFILE_FIELD_NAMES = {
  employment: 'situación laboral', contactTimes: 'horario', contactChannels: 'contacto', housing: 'vivienda', zones: 'zona', age: 'edad',
};

/**
 * Quita el filtro de perfil que la API rechazó (400 `field: pf_*`). Param
 * desconocido → quita todos los de perfil.
 * @returns {{ filters: object, label: string }}
 */
export function removeLeadProfileFilter(filters, field) {
  const f = filters || EMPTY_LEAD_ADVANCED_FILTERS;
  const key = LEAD_PROFILE_FILTER_FIELD_KEYS[field] || '';
  const profile = { ...EMPTY_LEAD_PROFILE_FILTERS, ...(f.profile || {}) };
  if (!key) return { filters: { ...f, profile: EMPTY_LEAD_PROFILE_FILTERS }, label: 'datos del cliente' };
  return { filters: { ...f, profile: { ...profile, [key]: EMPTY_LEAD_PROFILE_FILTERS[key] } }, label: PROFILE_FIELD_NAMES[key] };
}

/** Sin la sección de perfil (503 / flag): mismo estado sin `profile`. */
export function withoutLeadProfileFilters(filters) {
  return { ...(filters || EMPTY_LEAD_ADVANCED_FILTERS), profile: EMPTY_LEAD_PROFILE_FILTERS };
}

/** Clave estable de una zona del filtro (dedupe / chips). */
export const leadProfileZoneKey = (zone) => pfZoneToken(zone) || '';

/** Agrega una zona (sin repetir, hasta 20). */
export function addLeadProfileZone(zones, zone) {
  const list = Array.isArray(zones) ? zones : [];
  const key = leadProfileZoneKey(zone);
  if (!key || list.length >= LEAD_PF_ZONE_MAX || list.some((z) => leadProfileZoneKey(z) === key)) return list;
  return [...list, zone];
}

/** Texto corto de una zona del filtro (chips y resumen). */
export function describeLeadProfileZone(zone) {
  if (zone?.kind === 'other') return 'Otra zona (texto libre)';
  if (zone?.kind === 'none') return 'Sin zona';
  if (zone?.kind === 'province') return zone.name || zone.province || '';
  if (zone?.kind === 'municipality') return [zone.name, zone.province].filter(Boolean).join(', ');
  return [zone?.name, zone?.municipality || zone?.province].filter(Boolean).join(', ');
}

/** Estado de perfil → `query.profile` de `getLeads` (vacío → `null`). */
export function buildLeadProfileFilterQuery(profileFilters) {
  const p = { ...EMPTY_LEAD_PROFILE_FILTERS, ...(profileFilters || {}) };
  const band = LEAD_PROFILE_AGE_BANDS.find((b) => b.value === p.age);
  const out = {};
  LEAD_PROFILE_FILTER_GROUPS.forEach(({ key }) => { if (p[key]?.length) out[key] = p[key].slice(); });
  if (p.zones?.length) out.zones = p.zones.slice();
  if (band?.min != null) out.ageMin = band.min;
  if (band?.max != null) out.ageMax = band.max;
  return Object.keys(out).length ? out : null;
}

function describeLeadProfileFilters(profileFilters) {
  const p = { ...EMPTY_LEAD_PROFILE_FILTERS, ...(profileFilters || {}) };
  const labels = [];
  const band = LEAD_PROFILE_AGE_BANDS.find((b) => b.value === p.age);
  if (band) labels.push(`Edad ${band.label}`);
  LEAD_PROFILE_FILTER_GROUPS.forEach(({ key, summary, options }) => {
    const names = options.filter((o) => p[key]?.includes(o.value)).map((o) => o.label);
    if (!names.length) return;
    labels.push(`${summary}: ${names.length <= 2 ? names.join(' o ') : `${names.length} opciones`}`);
  });
  if (p.zones?.length) {
    labels.push(p.zones.length <= 2 ? `Zona: ${p.zones.map(describeLeadProfileZone).join(' o ')}` : `${p.zones.length} zonas`);
  }
  return labels;
}

function countLeadProfileFilters(profileFilters) {
  const p = { ...EMPTY_LEAD_PROFILE_FILTERS, ...(profileFilters || {}) };
  let n = LEAD_PROFILE_AGE_BANDS.some((b) => b.value === p.age) ? 1 : 0;
  LEAD_PROFILE_FILTER_GROUPS.forEach(({ key }) => { if (p[key]?.length) n += 1; });
  if (p.zones?.length) n += 1;
  return n;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const validDate = (v) => (DATE_RE.test(String(v || '')) ? String(v) : '');

/**
 * Red de seguridad (los inputs ya lo impiden): fechas al revés o futuras no
 * se mandan.
 */
export function hasLeadDateRangeError(filters, today = getLeadTodayAR()) {
  const from = validDate(filters?.from);
  const to = validDate(filters?.to);
  return Boolean((from && to && from > to) || from > today || to > today);
}

/** Rango efectivo: el del atajo activo, o Desde/Hasta a mano. */
export function resolveLeadDateRange(filters, today = getLeadTodayAR()) {
  const range = getLeadDatePresetRange(filters?.datePreset, today);
  if (range) return range;
  if (hasLeadDateRangeError(filters, today)) return { from: '', to: '' };
  return { from: validDate(filters?.from), to: validDate(filters?.to) };
}

/**
 * Query de `getLeads` para los filtros avanzados (sin `sort` ni registro).
 *
 * @param {typeof EMPTY_LEAD_ADVANCED_FILTERS} filters
 */
export function buildLeadAdvancedQuery(filters) {
  const f = filters || EMPTY_LEAD_ADVANCED_FILTERS;
  const query = {};
  // El panel filtra solo por última actividad; created_* queda en el servicio.
  const { from, to } = resolveLeadDateRange(f);
  if (from) query.updatedFrom = from;
  if (to) query.updatedTo = to;
  if (f.channels?.length) query.channels = f.channels.slice();
  const profile = buildLeadProfileFilterQuery(f.profile);
  if (profile) query.profile = profile;
  return query;
}

const formatDay = (iso) => {
  const [y, m, d] = String(iso).split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : iso;
};

/**
 * Etiquetas para el resumen (“6 de 11 leads · Alta 01/10/26–14/10/26 · WhatsApp”).
 *
 * @returns {string[]}
 */
export function describeLeadAdvancedFilters(filters) {
  const f = filters || EMPTY_LEAD_ADVANCED_FILTERS;
  const labels = [];
  const { from, to } = resolveLeadDateRange(f);
  if (from || to) {
    const field = 'Actividad';
    const preset = LEAD_DATE_PRESETS.find((x) => x.value === f.datePreset && x.summary);
    if (preset) labels.push(preset.standalone ? preset.summary : `${field}: ${preset.summary}`);
    else if (from && to) labels.push(`${field} ${formatDay(from)}–${formatDay(to)}`);
    else if (from) labels.push(`${field} desde ${formatDay(from)}`);
    else labels.push(`${field} hasta ${formatDay(to)}`);
  }
  const channelNames = LEAD_CHANNEL_FILTER_OPTIONS.filter((o) => f.channels?.includes(o.value)).map((o) => o.label);
  if (channelNames.length) labels.push(channelNames.length <= 2 ? channelNames.join(' o ') : `${channelNames.length} canales`);
  labels.push(...describeLeadProfileFilters(f.profile));
  return labels;
}

/**
 * Cantidad para el badge de “Filtros” (fecha y canal cuentan 1 c/u; cada grupo
 * de datos del cliente, 1; lote/unidad va en el buscador).
 */
export function countLeadAdvancedFilters(filters) {
  const f = filters || EMPTY_LEAD_ADVANCED_FILTERS;
  let n = 0;
  const range = resolveLeadDateRange(f);
  if (range.from || range.to) n += 1;
  if (f.channels?.length) n += 1;
  n += countLeadProfileFilters(f.profile);
  return n;
}
